import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { productSevice } from "./productService";
import axios from "axios";

export const getAllProducts = createAsyncThunk(
  "product/getAllProducts",
  async (data, thunkAPI) => {
    try {
      return await productSevice.getProducts(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getAllNewArrivals = createAsyncThunk(
  "product/getAllNewArrivals",
  async (data, thunkAPI) => {
    try {
      return await productSevice.getNewArrivals(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getLatestProducts = createAsyncThunk(
  "product/getLatestProducts",
  async (data, thunkAPI) => {
    try {
      return await productSevice.getLatestProducts(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);


export const getProCategories = createAsyncThunk(
  "product/getProCategories",
  async (data, thunkAPI) => {
    try {
      return await productSevice.getProCategories(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getAProduct = createAsyncThunk(
  "product/getAProduct",
  async (id, thunkAPI) => {
    try {
      return await productSevice.getSingleProduct(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addToWishlist = createAsyncThunk(
  "product/wishlist",
  async (wishData, thunkAPI) => {
    try {
      return await productSevice.addToWishlist(wishData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const orderProduct = createAsyncThunk(
  "product/orderProduct",
  async (data, thunkAPI) => {
    try {
      return await productSevice.order(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addRating = createAsyncThunk(
  "product/rating",
  async (data, thunkAPI) => {
    try {
      return await productSevice.rateProduct(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getCategories = createAsyncThunk(
  "product/getCategories", // Fixed unique name to avoid conflict with addRating
  async (data, thunkAPI) => {
    try {
      return await productSevice.getCategory(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

// New Cart-Related Thunks
const API_URL = "http://localhost:5000/api/cart";

export const getCartData = createAsyncThunk(
  "product/getCartData",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(API_URL, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data; // { status, message, data }
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeCart = createAsyncThunk(
  "product/removeCart",
  async (productIdObj, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/remove`, productIdObj, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data; // { status, message, data }
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const buyProduct = createAsyncThunk(
  "product/buyProduct",
  async (formData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/buy`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data; // Adjust based on actual buy endpoint response
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Updated Initial State
const productState = {
  product: "",
  latest: null,
  singleproduct: null,
  addToWishlist: null,
  rating: null,
  newArrivals: null,
  cart: [], 
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
};

export const productSlice = createSlice({
  name: "product",
  initialState: productState,
  reducers: {},
  extraReducers: (builder) => {
    // Existing Reducers
    builder
      .addCase(getAllProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.product = action.payload;
      })
      .addCase(getAllProducts.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.error;
      })
      .addCase(getAllNewArrivals.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllNewArrivals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.newArrivals = action.payload.products;
      })
      .addCase(getAllNewArrivals.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.error;
      })
      .addCase(getLatestProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getLatestProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.latest = action.payload.products;
      })
      .addCase(getLatestProducts.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.error;
      })
      .addCase(addToWishlist.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.addToWishlist = action.payload;
        state.message = "Product Added to Wishlist!";
      })
      .addCase(addToWishlist.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
      })
      .addCase(getAProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.singleproduct = action.payload;
        state.message = "Product Fetched Successfully";
      })
      .addCase(getAProduct.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.error;
      })
      .addCase(addRating.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addRating.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.rating = action.payload;
        state.message = "Rating Added Successfully";
        if (state.isSuccess) {
          // toast.success("Rating Added Successfully");
        }
      })
      .addCase(addRating.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.error;
      })
      // New Cart-Related Reducers
      .addCase(getCartData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCartData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.cart = action.payload.data ? [action.payload.data] : []; // Wrap in array to match ShoppingCart expectation
        state.message = action.payload.message;
      })
      .addCase(getCartData.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload?.message || action.error;
      })
      .addCase(removeCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.cart = [action.payload.data]; // Update cart with new data
        state.message = action.payload.message;
      })
      .addCase(removeCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload?.message || action.error;
      })
      .addCase(buyProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(buyProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.message = action.payload.message || "Purchase Successful";
      })
      .addCase(buyProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload?.message || action.error;
      });
  },
});

export default productSlice.reducer;